/**
 * Choix du mot de passe depuis le lien recu par courriel.
 *
 * Seul ecran du back-office atteignable sans etre connecte, avec la page de
 * connexion : le gerant qui arrive ici n'a pas encore de mot de passe. Le
 * jeton du lien tient lieu de preuve.
 *
 * Voir docs/adr/0024-inviter-le-gerant-par-courriel.md et docs/15, section 4.1.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { InvitationDTO } from '@scan2eat/shared';
import { PASSWORD_PATTERN } from '@scan2eat/shared';

import { ApiError, apiFetch } from '../api/client.js';
import { Icon } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import { labels } from '../labels.js';
import styles from './LoginPage.module.css';

type Status = 'checking' | 'ready' | 'done' | 'invalid' | 'expired';

export function InvitationPage() {
  const { token = '' } = useParams();
  const [status, setStatus] = useState<Status>('checking');
  const [invitation, setInvitation] = useState<InvitationDTO | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** Un refus du serveur decide de l'ecran : perime, ou simplement mort. */
  const applyFailure = useCallback((err: unknown) => {
    if (err instanceof ApiError && err.code === 'INVITATION_EXPIRED') setStatus('expired');
    else setStatus('invalid');
  }, []);

  useEffect(() => {
    apiFetch<InvitationDTO>(`/public/invitations/${token}`)
      .then((data) => { setInvitation(data); setStatus('ready'); })
      .catch(applyFailure);
  }, [token, applyFailure]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!PASSWORD_PATTERN.test(password)) {
      setError(labels.invitation.tooWeak);
      return;
    }
    if (password !== confirmation) {
      setError(labels.invitation.mismatch);
      return;
    }

    setSaving(true);
    try {
      await apiFetch(`/public/invitations/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      setStatus('done');
    } catch (err) {
      // Un lien mort ou perime pendant la saisie change l'ecran ; le reste
      // s'affiche sous le formulaire, qui reste utilisable.
      if (err instanceof ApiError
        && (err.code === 'INVITATION_INVALID' || err.code === 'INVITATION_EXPIRED')) {
        applyFailure(err);
        return;
      }
      setError(
        err instanceof ApiError
          ? (err.code === 'RATE_LIMITED' ? labels.auth.rateLimited : err.message)
          : labels.errors.network,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className={styles.screen}>
      <section className={styles.brand} aria-hidden="true">
        <Logo size={40} withWordmark tone="dark" />
        <div className={styles.brandBody}>
          <h2 className={styles.brandTitle}>{labels.auth.brandTitle}</h2>
          <p className={styles.brandText}>{labels.auth.brandText}</p>
        </div>
        <p className={styles.brandFoot}>{labels.app.name}</p>
      </section>

      <section className={styles.formSide}>
        {status === 'checking' && (
          <p className="page-subtitle" aria-busy="true">{labels.invitation.checking}</p>
        )}

        {(status === 'invalid' || status === 'expired') && (
          <div className={styles.form}>
            <span className={styles.mobileLogo}><Logo size={40} withWordmark /></span>
            <div role="alert" className="notice notice--warning" style={{ margin: 0 }}>
              <Icon name="alert" size={18} />
              <span className="grow">
                <strong>
                  {status === 'expired'
                    ? labels.invitation.expiredTitle
                    : labels.invitation.invalidTitle}
                </strong>
                <br />
                {status === 'expired'
                  ? labels.invitation.expiredText
                  : labels.invitation.invalidText}
              </span>
            </div>
            <Link to="/connexion" className="btn-primary btn-block btn-lg">
              {labels.invitation.goToLogin}
              <Icon name="arrow-right" size={18} />
            </Link>
          </div>
        )}

        {status === 'done' && (
          <div className={styles.form}>
            <span className={styles.mobileLogo}><Logo size={40} withWordmark /></span>
            <div role="status" className="notice notice--success" style={{ margin: 0 }}>
              <Icon name="check-circle" size={18} />
              <span className="grow">
                <strong>{labels.invitation.doneTitle}</strong>
                <br />
                {labels.invitation.doneText}
              </span>
            </div>
            <Link to="/connexion" className="btn-primary btn-block btn-lg">
              {labels.invitation.goToLogin}
              <Icon name="arrow-right" size={18} />
            </Link>
          </div>
        )}

        {status === 'ready' && invitation && (
          <form onSubmit={(e) => void submit(e)} className={styles.form}>
            <span className={styles.mobileLogo}><Logo size={40} withWordmark /></span>
            <div>
              <h1 className="text-title-1" style={{ margin: 0 }}>{labels.invitation.title}</h1>
              <p className="page-subtitle">{labels.invitation.intro(invitation.restaurantName)}</p>
            </div>

            <p style={{ margin: 0 }}>{labels.invitation.greeting(invitation.firstName)}</p>

            {/* L'adresse est montree, non modifiable : elle identifie le compte
                que cette invitation ouvre, et la changer n'aurait pas de sens. */}
            <div className="field">
              <label htmlFor="invitation-email" className="field-label">
                {labels.invitation.emailLabel}
              </label>
              <input
                id="invitation-email"
                className="input"
                value={invitation.email}
                readOnly
                autoComplete="username"
              />
            </div>

            {error && (
              <p role="alert" className="notice notice--danger" style={{ margin: 0 }}>
                <Icon name="alert" size={18} />
                {error}
              </p>
            )}

            <div className="field">
              <label htmlFor="invitation-password" className="field-label">
                {labels.invitation.password}
              </label>
              <input
                id="invitation-password"
                className="input"
                type="password"
                required
                autoFocus
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="field-hint">{labels.invitation.passwordRule}</p>
            </div>

            <div className="field">
              <label htmlFor="invitation-confirm" className="field-label">
                {labels.invitation.confirm}
              </label>
              <input
                id="invitation-confirm"
                className="input"
                type="password"
                required
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                aria-invalid={confirmation.length > 0 && confirmation !== password}
              />
            </div>

            <button type="submit" disabled={saving} className="btn-primary btn-block btn-lg">
              {saving ? labels.invitation.submitting : labels.invitation.submit}
              {!saving && <Icon name="arrow-right" size={18} />}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
